import { Buffer } from "buffer";

import moment from "moment-timezone";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import "react-toastify/dist/ReactToastify.css";

import blogimg from "assets/images/blog-img.webp";
import favicon from "assets/images/indianic-short-logo.svg";
import logo from "assets/images/logo.svg";
import profilepic from "assets/images/profile-pic.jpg";
import apiConfig from "configs/apiConfig";
import generalConfig from "configs/generalConfig";
import { DEFAULT_DATE_FORMAT, DEFAULT_LANGUAGE_CODE } from "utils/constants";
import { localStorageKeys } from "utils/localStorageKeys";

/******************* 
@Purpose : Used for show message notification
@Parameter : text, type, autoClose, position
@Author : INIC
******************/
export const messageNotification = (
  text,
  type = "success",
  autoClose = generalConfig.TOAST_TIMER,
  position = "top-right"
) => {
  toast[type](text, {
    position: position,
    autoClose: autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

/******************* 
@purpose : Used for encode data into base 64
@Parameter : {data} 
@Author : INIC
******************/
export const encodeBase64 = (data) => {
  return Buffer.from(data).toString("base64");
};

/******************* 
@purpose : Used for decode data from base 64
@Parameter : {data} 
@Author : INIC
******************/
export const decodeBase64 = (data) => {
  return Buffer.from(data, "base64").toString("ascii");
};

/******************* 
@purpose : Used for content type
@Parameter : {} 
@Author : IndiaNIC
******************/
export const getHeader = (formData = false) => {
  return {
    "Content-Type": formData ? "multipart/form-data" : "application/json",
    Accept: "application/json",
  };
};

/******************* 
@purpose : Used for File upload extension
@Parameter : {} 
@Author : INIC
******************/
export const FILE_UPLOAD_EXTENSION = [
  "image/png", // for .png image
  "image/jpeg", // for .jpeg image
  "image/jpg", // for .jpg image
  // ".svg", // for .svg image
];
export const SITE_IMAGE_UPLOAD_EXTENSION = [
  "png", // for .png image
  "jpeg", // for .jpeg image
  "jpg", // for .jpg image
  // ".svg", // for .svg image
];

/******************* 
@purpose : Used for Remember Me Login
@Parameter : {} 
@Author : INIC
******************/

export const rememberMe = (clearTheme = true) => {
  const rememberMe = localStorage?.getItem(localStorageKeys.REMEMBER_ME);
  if (!rememberMe) {
    localStorage.clear();
  } else {
    if (clearTheme) {
      localStorage.removeItem(localStorageKeys.THEME);
    }
    localStorage.removeItem(localStorageKeys.EXPIRATIONTIME);
    localStorage.removeItem(localStorageKeys.IS_EMAIL);
    localStorage.removeItem(localStorageKeys.IS_MOBILE);
    localStorage.removeItem(localStorageKeys.ma_la);
    localStorage.removeItem(localStorageKeys.MASKED_EMAIL);
    localStorage.removeItem(localStorageKeys.MASKED_PHONE);
    localStorage.removeItem(localStorageKeys.OTP_TOKEN);
    localStorage.removeItem(localStorageKeys?.VERIFY_LOGIN_TOKEN);
    localStorage.removeItem(localStorageKeys?.USER_ROLE);
    sessionStorage.clear();
  }
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : INIC
******************/

export const setDefaultProfilePic = (e) => {
  e.currentTarget.src = profilepic;
  e.currentTarget.onerror = null;
};

/******************* 
@purpose : Used for show default profile image onerror
@Parameter : {} 
@Author : INIC
******************/

export const setDefaultLogo = (e) => {
  e.currentTarget.src = logo;
  e.currentTarget.onerror = null;
};
export const setDefaultFavicon = (e) => {
  e.currentTarget.src = favicon;
  e.currentTarget.onerror = null;
};

export const arrayToJson = (array, key) => {
  let data = {};
  for (let obj of array) {
    data[obj[key]] = obj;
  }
  return data;
};

export const jsonToArray = (obj) => Object.values(obj);

/******************* 
@purpose : Used for formatting date 
@Parameter : {date, dateFormat} 
@Author : INIC
******************/
export const getFormattedDate = (date, dateFormat) => {
  if (date === "") {
    return false;
  }
  if (dateFormat) {
    return moment(date).format(dateFormat);
  } else {
    return moment(date).format(DEFAULT_DATE_FORMAT);
  }
};

/******************* 
@purpose : Used for formatting date in date picker date format prop
@Parameter : {date, dateFormat} 
@Author : INIC
******************/
export const getFormattedDateWithTime = (dateFormat, timeFormat) => {
  if (timeFormat === "12 hours") {
    return `${dateFormat} h:mm aa`;
  } else if (timeFormat === "24 hours") {
    return `${dateFormat} HH:mm`;
  } else {
    return `${dateFormat} h:mm aa`;
  }
};

/******************* 
@purpose : Used for converting date according to timezone
@Parameter : {date, timezone} 
@Author : INIC
******************/
export const getTimeZoneConvertedDate = (date, timezone) => {
  if (date === "") {
    return false;
  }
  return moment.tz(date, timezone);
};

/******************* 
@purpose : Used for converting date to utc
@Parameter : {date} 
@Author : INIC
******************/
export const getUTCFormat = (date) => {
  return moment(date).utc().format("YYYY-MM-DDTHH:mm:ss");
};

export const getFormattedTimeWithTimezone = (time, timezone, timeFormat) => {
  if (timeFormat === "12 hours") {
    return moment(time).tz(timezone).format("hh:mm A");
  } else if (timeFormat === "24 hours") {
    return moment(time).tz(timezone).format("HH:mm");
  } else {
    return moment(time).tz(timezone).format("hh:mm A");
  }
};

export const getFormattedDateTimeWithTimezone = (
  date,
  timezone,
  dateFormat,
  timeFormat
) => {
  const timeZoneString = timezone?.split(") ")[1];
  if (timeFormat === "12 hours") {
    return moment(date)?.tz(timeZoneString)?.format(`${dateFormat} hh:mm A`);
  } else if (timeFormat === "24 hours") {
    return moment(date)?.tz(timeZoneString)?.format(`${dateFormat} HH:mm`);
  } else {
    return moment(date)?.tz(timeZoneString)?.format(`${dateFormat} hh:mm A`);
  }
};

/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const fireDeletePopup = async (
  confirmDeleteHandler,
  resetGridRowSelection,
  title
) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to delete?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Delete",
    // cancelButtonText: "Cancel",
    // confirmButtonColor: "#A01B1B",
    // cancelButtonColor: "#999999",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
    resetGridRowSelection && resetGridRowSelection();
  }
};

/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const tabChangePopup = async (
  title,
  text,
  selectedClubTab,
  setSelectedClubTab
) => {
  const ChangesData = await Swal.fire({
    title: title || "Proceed with Changes?",
    text: text || "Switching tabs: Save changes or discard?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Save with changes",
    cancelButtonText: "Discard changes",
    confirmButtonColor: "#6362E6",
    cancelButtonColor: "#ED3E3E",
    reverseButtons: true,
    focusConfirm: true,
    focusCancel: false,
  });
  if (!ChangesData.isConfirmed) {
    setSelectedClubTab(selectedClubTab);
  }
  return ChangesData;
};

/******************* 
@purpose : Used for show alert popup 
@Parameter : {} 
@Author : INIC
******************/
export const deleteItemsPopup = async (confirmDeleteHandler, title) => {
  const willDelete = await Swal.fire({
    title: title || "Are you sure, you want to delete?",
    icon: "warning",
    buttons: true,
    dangerMode: true,
    showCancelButton: true,
    confirmButtonText: "Delete",
    // cancelButtonText: "Cancel",
    // confirmButtonColor: "#A01B1B",
    // cancelButtonColor: "#999999",
    reverseButtons: true,
    focusConfirm: false,
    focusCancel: true,
  });

  if (willDelete.isConfirmed) {
    confirmDeleteHandler();
  }
};

export const getLanguageOptions = (data) => {
  return data?.map((list) => ({
    label: `${list?.language}(${list?.country})`,
    value: list?._id,
  }));
};

export const getLanguageCode = (data, id) => {
  return id && data
    ? data?.find((list) => list?._id === id)?.languageCode
    : DEFAULT_LANGUAGE_CODE;
};

/******************* 
@purpose : Used for setting default pic in blog card view 
@Parameter : {} 
@Author : INIC
******************/
export const setDefaultBlogImage = (e) => {
  e.currentTarget.src = blogimg;
  e.currentTarget.onerror = null;
};
/******************* 
@purpose : Used for select Option in react select 
@Parameter : {} 
@Author : INIC
******************/
export const selectOptionHandler = (formik, name) => {
  return formik?.values?.[name]
    ? {
        label: formik?.values?.[name],
        value: formik?.values?.[name],
      }
    : undefined;
};

/******************* 
@purpose : Used for conditional ternary 
@Parameter : {} 
@Author : INIC
******************/
export const conditionalTernaryHandler = (condition, param1, param2) => {
  return condition ? param1 : param2;
};

/******************* 
@purpose : Used for common ternary 
@Parameter : {} 
@Author : INIC
******************/
export const commonTernaryHandler = (param, param2) => {
  return param ? param : param2;
};

/******************* 
@purpose : Used for download file
@Parameter : {type,file} 
@Author : INIC
******************/
export const downlaodFiletoType = async (type, filepath) => {
  let file = `${apiConfig.FIlE_UPLOAD}temp/${type}/${filepath}`;
  await fetch(file)
    .then((res) => res.blob())
    .then((data) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filepath);
      link.click();
    });
};

/******************* 
@purpose : Used for sorting array of objects
@Parameter : {arr, key} 
@Author : INIC
******************/
export const sortArrOfObj = (arr, key) => {
  return arr.sort((a, b) => {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });
};

export const dropdownOptions = (arrayObj, labelKey, valueKey) => {
  let updatedArray = [];
  let newArrayObj = arrayObj ? [...arrayObj] : [];
  if (newArrayObj?.length > 0) {
    updatedArray = newArrayObj?.map((obj, i) => {
      return {
        ...obj,
        label: newArrayObj[i][labelKey],
        value: newArrayObj[i][valueKey],
      };
    });
  }
  return updatedArray;
};

export const getImagesArray = (clubDetails) => {
  const otherImages = clubDetails?.otherImages || [];
  const maxOtherImages = 4;
  let isDetailsImageArray = [];
  for (let i = 0; i < maxOtherImages; i++) {
    isDetailsImageArray.push({
      image: otherImages[i] || "",
      isUploaded: !!otherImages[i],
    });
  }
  return isDetailsImageArray;
};

export const getCoverImagesArray = (clubDetails) => {
  const coverImages = Array.isArray(clubDetails?.coverImage)
    ? clubDetails.coverImage
    : clubDetails?.coverImage
    ? [clubDetails.coverImage]
    : [];
  const coverImagesHorizontal = Array.isArray(clubDetails?.coverImageHorizontal)
    ? clubDetails.coverImageHorizontal
    : clubDetails?.coverImageHorizontal
    ? [clubDetails.coverImageHorizontal]
    : [];
  const maxCoverImages = 5;
  let coverImageArray = [];
  for (let i = 0; i < maxCoverImages; i++) {
    coverImageArray.push({
      image: coverImages[i] || "",
      imageHorizontal: coverImagesHorizontal[i] || "",
      isUploaded: !!coverImages[i],
    });
  }
  return coverImageArray;
};
export const convertToExtendedTime = (timeString) => {
  // Parse the time string using Moment.js
  const parsedTime = moment(timeString, "HH:mm");

  // Format the time with the desired format "HH:mm:ss"
  const extendedTime = parsedTime.format("HH:mm:ss");

  return extendedTime;
};
export const getSelectedIds = (levelObject) => {
  if (levelObject?.activeBenefit?.benefitId) {
    return levelObject?.activeBenefit?.benefitId;
  } else if (levelObject?.activeBenefit?._id) {
    return levelObject?.activeBenefit?._id;
  } else {
    return "";
  }
};

//   currentAtmosphereRangeLastUpdatedAtLocal,
//   ianaTimeZoneID
// ) => {
//   // const currentTime = moment();
//   // const currentDateLocal = moment(moment().format("YYYY-MM-DD"));
//   // const AMTimeLocal = moment().set({ hour: 8, minute: 0, second: 0 });

//   // const lastUpdatedTime =
//   //   currentAtmosphereRangeLastUpdatedAt &&
//   //   moment.utc(currentAtmosphereRangeLastUpdatedAt).local();

//   // const lastUpdatedDate =
//   //   currentAtmosphereRangeLastUpdatedAt &&
//   //   moment(
//   //     moment
//   //       .utc(currentAtmosphereRangeLastUpdatedAt)
//   //       .local()
//   //       .format("YYYY-MM-DD")
//   //   );

//   const lastUpdatedTime =
//     currentAtmosphereRangeLastUpdatedAtLocal &&
//     moment(currentAtmosphereRangeLastUpdatedAtLocal);
//   const lastUpdatedDate =
//     currentAtmosphereRangeLastUpdatedAtLocal &&
//     moment(
//       moment(currentAtmosphereRangeLastUpdatedAtLocal).format("YYYY-MM-DD")
//     );

//   const currentTimeWithTimeZone = ianaTimeZoneID && moment().tz(ianaTimeZoneID);
//   const currentDateWithTimeZone =
//     ianaTimeZoneID && moment(moment().tz(ianaTimeZoneID).format("YYYY-MM-DD"));
//   const AMTimeLocal = moment().set({ hour: 8, minute: 0, second: 0 });
//   const AMTimeWithTimeZone = ianaTimeZoneID && AMTimeLocal.tz(ianaTimeZoneID);

//   if (currentDateWithTimeZone?.isSame(lastUpdatedDate, "day")) {
//     if (
//       currentTimeWithTimeZone?.isAfter(AMTimeWithTimeZone) &&
//       lastUpdatedTime?.isBefore(AMTimeWithTimeZone)
//     ) {
//       return false;
//     } else {
//       return true;
//     }
//   } else {
//     return false;
//   }
// };

// /** *****************
//   @purpose : Used For show atmos icon
//   @Parameter : {}
//   @Author : INIC
//   ***************** */
// export const isShowFalseVoyCount = (
//   currentFalseVoyLastUpdatedAtLocal,
//   ianaTimeZoneID
// ) => {
//   const lastUpdatedTime =
//     currentFalseVoyLastUpdatedAtLocal &&
//     moment(currentFalseVoyLastUpdatedAtLocal);
//   const lastUpdatedDate =
//     currentFalseVoyLastUpdatedAtLocal &&
//     moment(moment(currentFalseVoyLastUpdatedAtLocal).format("YYYY-MM-DD"));

//   const currentTimeWithTimeZone = ianaTimeZoneID && moment().tz(ianaTimeZoneID);
//   const currentDateWithTimeZone =
//     ianaTimeZoneID && moment(moment().tz(ianaTimeZoneID).format("YYYY-MM-DD"));
//   const AMTimeLocal = moment().set({ hour: 8, minute: 0, second: 0 });
//   const AMTimeWithTimeZone = ianaTimeZoneID && AMTimeLocal.tz(ianaTimeZoneID);

//   if (currentDateWithTimeZone?.isSame(lastUpdatedDate, "day")) {
//     if (
//       currentTimeWithTimeZone?.isAfter(AMTimeWithTimeZone) &&
//       lastUpdatedTime?.isBefore(AMTimeWithTimeZone)
//     ) {
//       return false;
//     } else {
//       return true;
//     }
//   } else {
//     return false;
//   }
// };

// export const isShowBasedOn08AM = (localDate, timezone) => {

//   let isShow = false;
//   const isSame = moment
//     .tz(localDate, timezone)
//     .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
//     .isSame(
//       moment.tz(timezone).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
//     );

//   if (isSame) {
//     if (
//       moment
//         .tz(timezone)
//         .isBefore(
//           moment
//             .tz(timezone)
//             .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
//         )
//     ) {
//       const startTime = moment
//         .tz(timezone)
//         .subtract(1, "day")
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       const endTime = moment
//         .tz(timezone)
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
//     } else {
//       const startTime = moment
//         .tz(timezone)
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       const endTime = moment
//         .tz(timezone)
//         .add(1, "day")
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
//     }
//   } else {
//     if (
//       moment
//         .tz(timezone)
//         .isBefore(
//           moment
//             .tz(timezone)
//             .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
//         )
//     ) {
//       const startTime = moment
//         .tz(timezone)
//         .subtract(1, "day")
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       const endTime = moment
//         .tz(timezone)
//         .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
//       isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
//     } else {
//       isShow = false;
//     }
//   }
//   return isShow;
// };

export const isShowBasedOn08AM = (localDate, timezone) => {
  let isShow = false;
  const isSame = moment
    .tz(localDate, timezone)
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .isSame(
      moment.tz(timezone).set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    );
  if (isSame) {
    if (
      moment
        .tz(timezone)
        .isBefore(
          moment
            .tz(timezone)
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        )
    ) {
      const startTime = moment
        .tz(timezone)
        .subtract(1, "day")
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      const endTime = moment
        .tz(timezone)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
    } else {
      const startTime = moment
        .tz(timezone)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      const endTime = moment
        .tz(timezone)
        .add(1, "day")
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
    }
  } else {
    if (
      moment
        .tz(timezone)
        .isBefore(
          moment
            .tz(timezone)
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
        )
    ) {
      const startTime = moment
        .tz(timezone)
        .subtract(1, "day")
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      const endTime = moment
        .tz(timezone)
        .set({ hour: 8, minute: 0, second: 0, millisecond: 0 });
      isShow = moment.tz(localDate, timezone).isBetween(startTime, endTime);
    } else {
      isShow = false;
    }
  }
  return isShow;
};

export const isFalseVoy = (falseVoyTime, timeZone) => {
  const givenTime = new Date(falseVoyTime);

  // Add one hour to the given time
  givenTime.setHours(givenTime.getHours() + 1);

  let getUTCFormattedString = givenTime.toISOString();

  // Get the current time
  const currentTime = new Date().toISOString("en-US", {
    timeZone: timeZone,
  });

  // Compare the times
  if (currentTime >= getUTCFormattedString) {
    return true;
  } else {
    return false;
  }
};

export const isPostAddBrand = (contentData) => {
  const regex = /<a[^>]*\s+href=['"]([^'"]+)['"][^>]*>.*?<\/a>/;
  const match = contentData.match(regex);
  let brandId = "";
  if (match) {
    brandId = match[1];
  } else {
    brandId = "";
  }
  return brandId;
};
export const isGetTaggedIds = (contentData) => {
  const regex = /<a\s+href='([0-9a-f-]+)'>[^<]+<\/a>/g;
  const matches = contentData.match(regex);
  let ids = [];
  if (matches) {
    // Extract IDs from the matches
    ids = matches.map((match) => {
      const idMatch = match.match(/<a\s+href='([0-9a-f-]+)'>/);
      if (idMatch && idMatch[1]) {
        return idMatch[1];
      }
      return null;
    });
  }
  return ids;
};
